import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';

import VerticalBarChart from '@/components/charts/VerticalBarChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import LoadingIndicator from '@/components/LoadingIndicator';
import { MONTH_DATE_FORMAT } from '@/constants/date.constants';
import useStyles from '@/pages/Administration/Devices/DeviceTrendChart/DeviceTrendChart.styles';

import { selectedDeviceDetailsAtom } from '../Devices.store';
import {
  getDeviceTypeLabel,
  useFetchDeviceEventsPerDay
} from './DeviceTrendChart.hooks';
import { deviceEventsByDateAtom } from './DeviceTrendChart.store';

const DeviceTrendChart = () => {
  const { classes } = useStyles();
  const [selectedDeviceDetail] = useAtom(selectedDeviceDetailsAtom);
  const { isFetching: isFetchingDeviceEvents } = useFetchDeviceEventsPerDay();
  const [deviceEventsByDate] = useAtom(deviceEventsByDateAtom);

  const chartData = deviceEventsByDate;

  const totalEvents = deviceEventsByDate.reduce(
    (a: number, b) => a + b.count,
    0
  );

  const averageValue = Math.round(totalEvents / 30 / 24);

  const numFormatter = new Intl.NumberFormat('en-US');

  const devicesTrendBarSeries: AgBarSeriesOptions[] = [
    {
      type: 'bar',
      xKey: 'date',
      yKey: 'count',
      yName: getDeviceTypeLabel(selectedDeviceDetail?.device_type?.id),
      fill: '#008ffb',
      tooltip: {
        renderer: (params) => {
          return `<div style="padding: 8px;">
                <span style="font-size: 12px;">${dayjs(params.datum.date).format(MONTH_DATE_FORMAT)}</span><br/>
                <span style="font-size: 12px;">${getDeviceTypeLabel(selectedDeviceDetail?.device_type?.id)}: ${new Intl.NumberFormat('en-US').format(Math.round(params.datum.count))}</span>
              </div>`;
        }
      }
    }
  ];

  return (
    <div className={classes.chartWrapper}>
      {isFetchingDeviceEvents ? (
        <LoadingIndicator noBackground isLoading />
      ) : (
        chartData && (
          <GenericChartWidget
            title={`Average ${getDeviceTypeLabel(selectedDeviceDetail?.device_type?.id)} Per Hour`}
            headerRightContent={
              <span>{numFormatter.format(averageValue)}</span>
            }
          >
            <VerticalBarChart
              data={deviceEventsByDate}
              series={devicesTrendBarSeries}
              height={50}
              suppressExponentialAxis
              suppressYAxis
              suppressXAxis
              suppressCrosshairLine
            />
          </GenericChartWidget>
        )
      )}
    </div>
  );
};

export default DeviceTrendChart;
